/**
 * Mounts the decryption-retry orchestrator at the top of the app and runs
 * the legacy-repair startup task once per client.
 *
 * Triggers wired here:
 *   - socket connect       -> retry pass over all failed messages
 *   - chatListSyncing 1->0 -> retry pass over all failed messages
 *   - route change         -> retry pass scoped to the new active chat
 *
 * The "Retry" button inside the report dialog calls
 * `runDecryptionRetryPass` directly.
 */

import { useEffect, useRef } from "react";
import { useChatListState } from "@/zustand/ChatListState";
import { useConnectionState } from "@/zustand/ConnectionState";
import { useNavigationState } from "@/zustand/NavigationState";
import { runDecryptionRetryPass } from "@/middleware/decryptionRetryOrchestrator";
import { runLegacyDecryptionRepair } from "@/middleware/legacyDecryptionRepair";
import { runLegacyUndecryptableCleanup } from "@/middleware/legacyUndecryptableCleanup";
import { featureFlags } from "@/config/featureFlags";

export const useDecryptionRetryBootstrap = (): void => {
  const bootRanRef = useRef(false);

  // One-shot startup: run the legacy repair pass.
  useEffect(() => {
    if (!featureFlags.decryptionRetry || bootRanRef.current) {
      return;
    }
    bootRanRef.current = true;
    // Repair first (it rewrites legacy sentinel rows into the failed+no-ciphertext
    // class), THEN delete that class + reset the affected chats so the bulk loader
    // re-pages them — instead of the orchestrator refetching one-by-one (storm).
    void runLegacyDecryptionRepair()
      .then(() => runLegacyUndecryptableCleanup())
      .catch(() => undefined);
  }, []);

  // Subscribe to chatListSyncing transitions: only fire on true -> false.
  useEffect(() => {
    if (!featureFlags.decryptionRetry) {
      return;
    }
    let prev = useChatListState.getState().chatListSyncing;
    const unsubscribe = useChatListState.subscribe((state) => {
      const next = state.chatListSyncing;
      if (prev === true && next === false) {
        void runDecryptionRetryPass().catch(() => undefined);
      }
      prev = next;
    });

    // Subscribe to socket connect events through the connection store. The
    // store already exposes the live socket; we attach a listener whenever
    // it changes. If the socket is already connected when we attach
    // (common on hook remounts / persisted singleton sockets), fire the
    // retry pass immediately so we don't have to wait for the next
    // reconnect to heal failed rows.
    const handleSocket = (
      socket: ReturnType<typeof useConnectionState.getState>["socket"],
    ): (() => void) => {
      if (!socket) {
        return () => undefined;
      }
      const onConnect = () => {
        void runDecryptionRetryPass().catch(() => undefined);
      };
      socket.on("connect", onConnect);
      if (socket.connected) {
        onConnect();
      }
      return () => {
        socket.off("connect", onConnect);
      };
    };

    // Track the current socket reference. ConnectionState updates fire on
    // every connection-status / timestamp change; we only want to rewire
    // when the underlying socket instance itself swaps out.
    let currentSocket = useConnectionState.getState().socket;
    let socketCleanup = handleSocket(currentSocket);
    const unsubscribeSocket = useConnectionState.subscribe((state) => {
      if (state.socket === currentSocket) {
        return;
      }
      socketCleanup();
      currentSocket = state.socket;
      socketCleanup = handleSocket(currentSocket);
    });

    return () => {
      unsubscribe();
      unsubscribeSocket();
      socketCleanup();
    };
  }, []);

  // Trigger #3: route change into a chat that has failed messages.
  const activeChatId = useNavigationState((s) => s.activeChatId);
  useEffect(() => {
    if (!featureFlags.decryptionRetry || !activeChatId) {
      return;
    }
    void runDecryptionRetryPass(activeChatId).catch(() => undefined);
  }, [activeChatId]);
};
